// TODO: Stop using react DOM and move to a react native solution
// TODO: Make the logo loading use the same logic as the assets! Messy solution for now.

import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { toPng } from "html-to-image";
import { TTemplate } from "../../../../../utils/types";
import { resizeImage } from "../../../../../utils/image";
// import { downloadImage } from "../../../../../utils/downloadUtils";
import {
  getSideBySideImagePutUrls,
  getAssetsByIds,
  createSideBySideManualPairs,
  markSideBySideImagesUploaded,
} from "../../../../../services/api-clinic";
import {
  CLINIC_OS_ASSET_BASE,
  R2_ASSET_BASE,
  toCanonicalStickerSrc,
} from "../../../../../config/stickerPresets";
import { Rnd } from "react-rnd";

// Define the operation type
type OperationType = "idle" | "saving" | "downloading" | "downloadingAll";

type DrawAllImagesProps = {
  templatesData: {
    template: TTemplate;
    afterCroppedImage: string;
    beforeCroppedImage: string;
    afterOriginalImage: string;
    beforeOriginalImage: string;
    afterDimensions: { width: number; height: number };
    beforeDimensions: { width: number; height: number };
    afterCropCoords: {
      cropX: number;
      cropY: number;
      cropWidth: number;
      cropHeight: number;
    };
    beforeCropCoords: {
      cropX: number;
      cropY: number;
      cropWidth: number;
      cropHeight: number;
    };
    beforeImageId: number;
    afterImageId: number;
    concat: {
      id_in_database: number;
    };
  }[];
  drawAllImages: boolean;
  setDrawAllImages: (drawAllImages: boolean) => void;
  session: any;
  operationType: OperationType;
  onImageGenerated?: (dataUrl: string) => void;
  onRenderError?: (message: string) => void;
  selectedIndex?: number;
  batchUuid?: string;
  onManualPairResolved?: (templateIndex: number, concatId: number) => void;
};

const TRANSPARENT_PIXEL_DATA_URL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
const EXPORT_PREFETCH_MAX_ATTEMPTS_PER_URL = 2;
const EXPORT_PREFETCH_RETRY_DELAY_MS = 200;

const getAlternateAssetUrl = (src: string) => {
  if (src.startsWith(CLINIC_OS_ASSET_BASE)) {
    return src.replace(CLINIC_OS_ASSET_BASE, R2_ASSET_BASE);
  }
  if (src.startsWith(R2_ASSET_BASE)) {
    return src.replace(R2_ASSET_BASE, CLINIC_OS_ASSET_BASE);
  }
  return null;
};

const summarizeUrlForLogs = (src?: string) => {
  if (!src) return src;
  try {
    const parsed = new URL(src);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return src.length > 200 ? `${src.slice(0, 200)}...` : src;
  }
};

type TOverlayTemplateImage = TTemplate["images"][number];

type TResolvedOverlay = {
  sourceUrl: string;
  sourceType: "assetId" | "src";
  assetId: number | null;
  overlayKind: string;
  originalSrc: string;
};

const isRemoteHttpUrl = (src?: string) => {
  if (!src || src.startsWith("blob:") || src.startsWith("data:")) {
    return false;
  }
  return /^https?:\/\//i.test(src);
};

const isTemplatePlaceholderImage = (src?: string) => {
  if (!src) return false;
  return (
    src.includes("TEMPLATE_LOGO_SQUARE") ||
    src.includes("TEMPLATE_LOGO_RECTANGLE")
  );
};

const resolveOverlayForExport = (
  additionalImage: TOverlayTemplateImage,
  assetUrlMap: Record<number, string>,
): TResolvedOverlay | null => {
  if (isTemplatePlaceholderImage(additionalImage.src)) {
    return null;
  }

  const assetId =
    typeof additionalImage.assetId === "number"
      ? additionalImage.assetId
      : null;
  const originalSrc = additionalImage.src ?? "";
  const rawSource =
    assetId !== null ? assetUrlMap[assetId] || originalSrc : originalSrc;
  if (!rawSource) {
    return null;
  }

  return {
    sourceUrl: toCanonicalStickerSrc(rawSource) || rawSource,
    sourceType: assetId !== null ? "assetId" : "src",
    assetId,
    overlayKind: additionalImage.kind || "standard",
    originalSrc,
  };
};

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const DrawAllImages: React.FC<DrawAllImagesProps> = ({
  templatesData,
  drawAllImages,
  setDrawAllImages,
  session,
  operationType,
  onImageGenerated,
  onRenderError,
  selectedIndex = 0,
  batchUuid,
  onManualPairResolved,
}) => {
  const [isRendered, setIsRendered] = useState(false);
  const [assetUrlMap, setAssetUrlMap] = useState<Record<number, string>>({});
  const [assetsReady, setAssetsReady] = useState(false);
  const [externalImageUrlMap, setExternalImageUrlMap] = useState<
    Record<string, string>
  >({});
  const [externalAssetsReady, setExternalAssetsReady] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const portalContainer = useRef<HTMLDivElement | null>(null);
  const hasReportedRenderErrorRef = useRef(false);
  const onRenderErrorRef = useRef(onRenderError);
  const externalObjectUrlsRef = useRef<string[]>([]);
  const loggedResolvedOverlaySrcRef = useRef<Set<string>>(new Set());
  const loggedOverlayErrorSrcRef = useRef<Set<string>>(new Set());

  const logExportDebug = useCallback(
    (event: string, payload: Record<string, unknown>) => {
      console.log(`[BA_EXPORT_DEBUG] ${event}`, {
        operationType,
        drawAllImages,
        ...payload,
      });
    },
    [operationType, drawAllImages],
  );

  useEffect(() => {
    onRenderErrorRef.current = onRenderError;
  }, [onRenderError]);

  const reportRenderError = useCallback(
    (message: string, error?: unknown) => {
      if (hasReportedRenderErrorRef.current) {
        return;
      }
      hasReportedRenderErrorRef.current = true;

      if (error) {
        console.error(message, error);
      } else {
        console.error(message);
      }
      logExportDebug("report_render_error", { message });
      onRenderErrorRef.current?.(message);
    },
    [logExportDebug],
  );

  const revokeExternalObjectUrls = useCallback(() => {
    externalObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    externalObjectUrlsRef.current = [];
  }, []);

  useEffect(() => {
    return () => {
      revokeExternalObjectUrls();
    };
  }, [revokeExternalObjectUrls]);

  useEffect(() => {
    if (drawAllImages) {
      hasReportedRenderErrorRef.current = false;
      loggedResolvedOverlaySrcRef.current.clear();
      loggedOverlayErrorSrcRef.current.clear();
    }
  }, [drawAllImages, operationType]);

  // Create portal container on mount
  useEffect(() => {
    // Create a container that will be in the actual DOM
    const div = document.createElement("div");
    div.id = "template-render-container";
    div.style.position = "fixed";
    div.style.left = "0";
    div.style.top = "0";
    div.style.width = "1000px";
    div.style.height = "1000px";
    div.style.pointerEvents = "none";
    div.style.opacity = "0.01"; // Almost invisible but still rendered
    div.style.zIndex = "-1000";
    div.style.overflow = "hidden";
    document.body.appendChild(div);

    portalContainer.current = div;

    return () => {
      if (document.body.contains(div)) {
        document.body.removeChild(div);
      }
    };
  }, []);

  const base64ToBlob = (
    base64Data: string,
    contentType = "",
    sliceSize = 512,
  ): Blob => {
    const byteCharacters = atob(base64Data.split(",")[1]); // Split and decode base64 string
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = Array.from(slice, (char) => char.charCodeAt(0));
      byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: contentType });
  };

  useEffect(() => {
    let isMounted = true;

    const fetchAssetUrls = async () => {
      if (!drawAllImages) {
        setAssetUrlMap({});
        setAssetsReady(false);
        return;
      }

      if (!session) {
        setAssetUrlMap({});
        setAssetsReady(true);
        return;
      }

      const assetIds = Array.from(
        new Set(
          templatesData.flatMap((t) =>
            (t.template.images ?? [])
              .map((img) => img.assetId)
              .filter((id): id is number => typeof id === "number"),
          ),
        ),
      );

      if (!assetIds.length) {
        setAssetUrlMap({});
        setAssetsReady(true);
        return;
      }

      logExportDebug("asset_url_fetch_start", {
        assetIdCount: assetIds.length,
      });
      setAssetsReady(false);

      try {
        const response = await getAssetsByIds(session, assetIds);

        if (!isMounted) {
          return;
        }

        const map = Object.fromEntries(
          response.assets.map((a) => [a.asset_id, a.get_url]),
        );
        setAssetUrlMap(map);
        logExportDebug("asset_url_fetch_success", {
          returnedAssetCount: Object.keys(map).length,
        });
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch asset URLs:", error);
          logExportDebug("asset_url_fetch_failed", {
            error:
              error instanceof Error
                ? error.message
                : "unknown asset url error",
          });
        }
      } finally {
        if (isMounted) {
          setAssetsReady(true);
        }
      }
    };

    fetchAssetUrls();

    return () => {
      isMounted = false;
    };
  }, [drawAllImages, session, templatesData, logExportDebug]);

  useEffect(() => {
    let isMounted = true;

    const fetchExternalImages = async () => {
      if (!drawAllImages) {
        revokeExternalObjectUrls();
        setExternalImageUrlMap({});
        setExternalAssetsReady(false);
        return;
      }

      const prefetchCandidatesByUrl = new Map<string, TResolvedOverlay>();
      templatesData.forEach((templateData) => {
        (templateData.template.images ?? []).forEach((additionalImage) => {
          const resolvedOverlay = resolveOverlayForExport(
            additionalImage,
            assetUrlMap,
          );
          if (!resolvedOverlay || !isRemoteHttpUrl(resolvedOverlay.sourceUrl)) {
            return;
          }

          if (!prefetchCandidatesByUrl.has(resolvedOverlay.sourceUrl)) {
            prefetchCandidatesByUrl.set(
              resolvedOverlay.sourceUrl,
              resolvedOverlay,
            );
          }
        });
      });

      const prefetchCandidates = Array.from(prefetchCandidatesByUrl.values());

      logExportDebug("external_prefetch_candidates", {
        count: prefetchCandidates.length,
        overlays: prefetchCandidates.map((candidate) => ({
          overlayKind: candidate.overlayKind,
          overlayAssetId: candidate.assetId,
          sourceType: candidate.sourceType,
          src: summarizeUrlForLogs(candidate.originalSrc),
          resolvedSrc: summarizeUrlForLogs(candidate.sourceUrl),
        })),
      });

      if (!prefetchCandidates.length) {
        revokeExternalObjectUrls();
        setExternalImageUrlMap({});
        setExternalAssetsReady(true);
        return;
      }

      setExternalAssetsReady(false);

      try {
        const objectUrls: string[] = [];
        const results = await Promise.all(
          prefetchCandidates.map(async (candidate) => {
            const fallbackUrl = getAlternateAssetUrl(candidate.sourceUrl);
            const candidateUrls = fallbackUrl
              ? [candidate.sourceUrl, fallbackUrl]
              : [candidate.sourceUrl];
            let lastError = "unknown prefetch error";

            for (const candidateUrl of candidateUrls) {
              for (
                let attempt = 1;
                attempt <= EXPORT_PREFETCH_MAX_ATTEMPTS_PER_URL;
                attempt++
              ) {
                try {
                  logExportDebug("overlay_prefetch_attempt", {
                    overlayKind: candidate.overlayKind,
                    overlayAssetId: candidate.assetId,
                    sourceType: candidate.sourceType,
                    src: summarizeUrlForLogs(candidate.originalSrc),
                    resolvedSrc: summarizeUrlForLogs(candidate.sourceUrl),
                    requestUrl: summarizeUrlForLogs(candidateUrl),
                    attempt,
                    maxAttempts: EXPORT_PREFETCH_MAX_ATTEMPTS_PER_URL,
                  });

                  const response = await fetch(candidateUrl, {
                    mode: "cors",
                    credentials: "omit",
                    cache: "no-store",
                  });
                  if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                  }
                  const blob = await response.blob();
                  if (!blob.size) {
                    throw new Error("Fetched overlay blob is empty");
                  }

                  const objectUrl = URL.createObjectURL(blob);
                  objectUrls.push(objectUrl);

                  logExportDebug("overlay_prefetch_success", {
                    overlayKind: candidate.overlayKind,
                    overlayAssetId: candidate.assetId,
                    sourceType: candidate.sourceType,
                    src: summarizeUrlForLogs(candidate.originalSrc),
                    resolvedSrc: summarizeUrlForLogs(candidate.sourceUrl),
                    requestUrl: summarizeUrlForLogs(candidateUrl),
                    objectUrlScheme: "blob",
                  });
                  return [candidate.sourceUrl, objectUrl] as const;
                } catch (error) {
                  lastError =
                    error instanceof Error
                      ? error.message
                      : "unknown prefetch error";
                  logExportDebug("overlay_prefetch_attempt_failed", {
                    overlayKind: candidate.overlayKind,
                    overlayAssetId: candidate.assetId,
                    sourceType: candidate.sourceType,
                    src: summarizeUrlForLogs(candidate.originalSrc),
                    resolvedSrc: summarizeUrlForLogs(candidate.sourceUrl),
                    requestUrl: summarizeUrlForLogs(candidateUrl),
                    attempt,
                    maxAttempts: EXPORT_PREFETCH_MAX_ATTEMPTS_PER_URL,
                    error: lastError,
                  });
                  if (attempt < EXPORT_PREFETCH_MAX_ATTEMPTS_PER_URL) {
                    await wait(EXPORT_PREFETCH_RETRY_DELAY_MS * attempt);
                  }
                }
              }
            }

            logExportDebug("overlay_prefetch_fallback_transparent", {
              overlayKind: candidate.overlayKind,
              overlayAssetId: candidate.assetId,
              sourceType: candidate.sourceType,
              src: summarizeUrlForLogs(candidate.originalSrc),
              resolvedSrc: summarizeUrlForLogs(candidate.sourceUrl),
              fallbackSrcType: "transparent_data_url",
              error: lastError,
            });
            return [candidate.sourceUrl, TRANSPARENT_PIXEL_DATA_URL] as const;
          }),
        );

        if (!isMounted) {
          objectUrls.forEach((url) => URL.revokeObjectURL(url));
          return;
        }

        revokeExternalObjectUrls();
        externalObjectUrlsRef.current = objectUrls;
        setExternalImageUrlMap(Object.fromEntries(results));
      } catch (error) {
        logExportDebug("external_prefetch_unhandled_error", {
          error:
            error instanceof Error ? error.message : "unknown prefetch error",
        });
        if (isMounted) {
          setExternalImageUrlMap({});
        }
      } finally {
        if (isMounted) {
          setExternalAssetsReady(true);
        }
      }
    };

    fetchExternalImages();

    return () => {
      isMounted = false;
    };
  }, [
    drawAllImages,
    templatesData,
    assetUrlMap,
    logExportDebug,
    revokeExternalObjectUrls,
  ]);

  const waitForImages = async (container: HTMLElement) => {
    const images = Array.from(container.querySelectorAll("img"));

    await Promise.all(
      images.map((img) => {
        if (img.complete) {
          return Promise.resolve();
        }

        return new Promise<void>((resolve) => {
          const handleFinish = () => {
            img.removeEventListener("load", handleFinish);
            img.removeEventListener("error", handleFinish);
            resolve();
          };

          img.addEventListener("load", handleFinish);
          img.addEventListener("error", handleFinish);
        });
      }),
    );
  };

  // Wait for render to complete before handling export
  useEffect(() => {
    if (
      drawAllImages &&
      templatesData.length > 0 &&
      assetsReady &&
      externalAssetsReady
    ) {
      console.log("DrawAllImages triggered with operation:", operationType);

      // Give React time to render the components
      const timer = setTimeout(() => {
        setIsRendered(true);
      }, 500);

      return () => clearTimeout(timer);
    } else {
      setIsRendered(false);
    }
  }, [
    drawAllImages,
    templatesData,
    operationType,
    assetsReady,
    externalAssetsReady,
  ]);

  const handleExport = async () => {
    if (!templatesData.length || !session) return;

    try {
      console.log(`Executing operation: ${operationType}`);

      if (
        operationType === "downloading" ||
        operationType === "downloadingAll"
      ) {
        // For download operations, we render the selected template
        const templateContainers = document.querySelectorAll(
          ".draw-template-container",
        );

        if (templateContainers.length === 0) {
          console.error("No template containers found in DOM");
          reportRenderError(
            "⚠️ Failed to render image for download. Please try again.",
          );
          return;
        }

        // Use the selected index or default to 0
        const indexToDownload =
          selectedIndex < templateContainers.length ? selectedIndex : 0;

        const container = templateContainers[indexToDownload] as HTMLElement;

        try {
          // Dynamically calculate pixel ratio to scale to 1000px tall
          const targetHeight = 1000;
          const actualHeight = container.offsetHeight;
          const ratio = targetHeight / actualHeight;

          await waitForImages(container);

          const dataUrl = await toPng(container, {
            cacheBust: false,
            pixelRatio: ratio,
          });

          // Use the utility function for smart downloading
          // TODO: Uncomment this when ready to use after testing ios download to photos
          // await downloadImage(dataUrl, {
          //   filename: `before-after-${Date.now()}.png`,
          //   title: "Save Before & After Image",
          //   customInstructions: `
          //     <p><strong>To save your before & after image to Photos:</strong></p>
          //     <p>1. Tap and hold the image above</p>
          //     <p>2. Select "Save to Photos" from the menu</p>
          //     <p style="margin-top: 15px; font-size: 14px; color: #666;">
          //       Your image will be saved to your Photos app where you can share it with others!
          //     </p>
          //   `
          // });

          // Call the callback with the data URL
          if (onImageGenerated) {
            onImageGenerated(dataUrl);
          }
        } catch (error) {
          console.error(`Error processing image for download:`, error);
          reportRenderError(
            "⚠️ Failed to render image for download. Please try again.",
            error,
          );
        }
      } else if (operationType === "saving") {
        // For save operations, we upload to S3.
        //
        // Templates fall into two groups:
        //  1. Existing pairs (concat.id_in_database > 0) — fetch put URLs via
        //     the standard images/put-urls endpoint.
        //  2. Manual pairs (concat.id_in_database < 0) — first call the
        //     add-manual-pairs-to-batch endpoint to create real concat
        //     records, then upload to the put URLs returned in that response.

        const existingTemplateIndexes: number[] = [];
        const manualTemplateIndexes: number[] = [];
        templatesData.forEach((templateData, index) => {
          if (templateData.concat.id_in_database > 0) {
            existingTemplateIndexes.push(index);
          } else if (
            templateData.beforeImageId > 0 &&
            templateData.afterImageId > 0
          ) {
            manualTemplateIndexes.push(index);
          }
        });

        if (
          existingTemplateIndexes.length === 0 &&
          manualTemplateIndexes.length === 0
        ) {
          console.warn("No saveable images found.");
          if (onImageGenerated) {
            onImageGenerated(TRANSPARENT_PIXEL_DATA_URL);
          }
          return;
        }

        // Per-template-index resolved put URLs.
        const putUrlByTemplateIndex: Record<number, string> = {};
        const thumbnailPutUrlByTemplateIndex: Record<number, string> = {};
        // For manual pairs only: track the concat DB id per template index so
        // we can mark them uploaded on the backend after the PUTs succeed.
        const manualConcatIdByTemplateIndex: Record<number, number> = {};
        const successfullyUploadedManualConcatIds: number[] = [];

        if (existingTemplateIndexes.length > 0) {
          const existingImageIds = existingTemplateIndexes.map(
            (index) => templatesData[index].concat.id_in_database,
          );
          const uploadUrls = await getSideBySideImagePutUrls(
            session,
            existingImageIds,
          );
          existingTemplateIndexes.forEach((templateIndex, i) => {
            putUrlByTemplateIndex[templateIndex] = uploadUrls.put_urls[i];
            thumbnailPutUrlByTemplateIndex[templateIndex] =
              uploadUrls.thumbnail_put_urls[i];
          });
        }

        if (manualTemplateIndexes.length > 0) {
          if (!batchUuid) {
            console.error("Cannot create manual pairs: missing batchUuid");
            reportRenderError(
              "⚠️ Failed to save manual pairs (missing batch id). Please try again.",
            );
          } else {
            try {
              const manualPairsRequest = manualTemplateIndexes.map(
                (templateIndex) => ({
                  before_image_id: templatesData[templateIndex].beforeImageId,
                  after_image_id: templatesData[templateIndex].afterImageId,
                }),
              );
              const manualPairsResponse = await createSideBySideManualPairs(
                session,
                batchUuid,
                manualPairsRequest,
              );

              // Map response back to template indexes by (before_id, after_id).
              const responseByKey = new Map<
                string,
                (typeof manualPairsResponse.pairs)[number]
              >();
              manualPairsResponse.pairs.forEach((p) => {
                responseByKey.set(
                  `${p.before_image_id}-${p.after_image_id}`,
                  p,
                );
              });

              manualTemplateIndexes.forEach((templateIndex) => {
                const t = templatesData[templateIndex];
                const created = responseByKey.get(
                  `${t.beforeImageId}-${t.afterImageId}`,
                );
                if (!created) {
                  console.warn(
                    `No manual pair response for template ${templateIndex}`,
                  );
                  return;
                }
                putUrlByTemplateIndex[templateIndex] = created.concat_put_url;
                thumbnailPutUrlByTemplateIndex[templateIndex] =
                  created.concat_thumbnail_put_url;
                manualConcatIdByTemplateIndex[templateIndex] =
                  created.concat_image_id;
                if (onManualPairResolved) {
                  onManualPairResolved(templateIndex, created.concat_image_id);
                }
              });
            } catch (error) {
              console.error("Failed to create manual pairs", error);
              reportRenderError(
                "⚠️ Failed to create manual pairs on the server. Please try again.",
                error,
              );
            }
          }
        }

        const saveableTemplateIndexes = [
          ...existingTemplateIndexes,
          ...manualTemplateIndexes,
        ].filter((index) => putUrlByTemplateIndex[index]);

        // Check for container elements
        const templateContainers = document.querySelectorAll(
          ".draw-template-container",
        );

        if (templateContainers.length === 0) {
          console.error("No template containers found in DOM");
          reportRenderError(
            "⚠️ Failed to render image for save. Please try again.",
          );
          return;
        }

        for (let i = 0; i < saveableTemplateIndexes.length; i++) {
          const templateIndex = saveableTemplateIndexes[i];
          const container = templateContainers[templateIndex] as HTMLElement;
          if (!container) {
            console.warn(
              `No template container found for index ${templateIndex}`,
            );
            reportRenderError(
              "⚠️ Failed to render one or more images for save. Please try again.",
            );
            continue;
          }

          try {
            // Dynamically calculate pixel ratio to scale to 1000px tall
            const targetHeight = 1000;
            const actualHeight = container.offsetHeight;
            const ratio = targetHeight / actualHeight;

            console.log(
              `Original height: ${actualHeight}px → Scaling with pixelRatio: ${ratio.toFixed(2)}`,
            );

            await waitForImages(container);

            const dataUrl = await toPng(container, {
              cacheBust: false,
              pixelRatio: ratio,
              skipFonts: true,
            });
            console.log(`🖼️ Image rendered for index ${i}`);

            // Upload full-size image
            const putUrl = putUrlByTemplateIndex[templateIndex];
            const thumbnailPutUrl =
              thumbnailPutUrlByTemplateIndex[templateIndex];
            if (putUrl) {
              const blob = base64ToBlob(dataUrl, "image/png");

              const res = await fetch(putUrl, {
                method: "PUT",
                headers: {
                  "Content-Type": "image/png",
                },
                body: blob,
              });

              if (!res.ok) {
                console.error(`Failed to upload image ${i}:`, res.statusText);
              } else {
                console.log(`✅ Image ${i} uploaded to S3`);
                const manualConcatId =
                  manualConcatIdByTemplateIndex[templateIndex];
                if (manualConcatId) {
                  successfullyUploadedManualConcatIds.push(manualConcatId);
                }
              }

              // Generate and upload thumbnail
              if (thumbnailPutUrl) {
                const thumbnailDataUrl = await resizeImage(dataUrl, 160);
                const thumbnailBlob = base64ToBlob(
                  thumbnailDataUrl,
                  "image/png",
                );

                const thumbnailRes = await fetch(thumbnailPutUrl, {
                  method: "PUT",
                  headers: {
                    "Content-Type": "image/png",
                  },
                  body: thumbnailBlob,
                });

                if (!thumbnailRes.ok) {
                  console.error(
                    `Failed to upload thumbnail ${i}:`,
                    thumbnailRes.statusText,
                  );
                } else {
                  console.log(`✅ Thumbnail ${i} uploaded successfully`);
                }
              }
            } else {
              console.warn(`❌ No put_url for image ${i}`);
            }
          } catch (error) {
            console.error(`Error processing image ${i}:`, error);
            reportRenderError(
              "⚠️ Failed to process one or more images. Please try again.",
              error,
            );
          }
        }

        // For manual pairs, the standard S3-event lambda flow doesn't apply
        // (those concat records were created on-demand). Explicitly mark them
        // uploaded so they show up immediately in galleries.
        if (successfullyUploadedManualConcatIds.length > 0) {
          try {
            await markSideBySideImagesUploaded(
              session,
              successfullyUploadedManualConcatIds,
            );
            console.log(
              `✅ Marked ${successfullyUploadedManualConcatIds.length} manual-pair concat image(s) as uploaded`,
            );
          } catch (error) {
            console.error(
              "Failed to mark manual-pair concat images as uploaded",
              error,
            );
          }
        }

        // After all images are uploaded, call the callback to signal completion
        if (onImageGenerated) {
          // We just need to call it with any data to signal completion
          // Using a small empty transparent png as the data
          onImageGenerated(TRANSPARENT_PIXEL_DATA_URL);
        }
      }
    } catch (error) {
      console.error("Error in export process:", error);
      reportRenderError("⚠️ Failed to export image. Please try again.", error);
    } finally {
      setIsRendered(false);
      setDrawAllImages(false);
    }
  };

  // Process export when rendering is confirmed
  useEffect(() => {
    if (isRendered) {
      handleExport();
    }
  }, [isRendered]);

  // Determine which templates to render
  let templatesToRender = templatesData;

  // For download operations, only render the selected template
  if (operationType === "downloading" || operationType === "downloadingAll") {
    templatesToRender = [
      templatesData[selectedIndex < templatesData.length ? selectedIndex : 0],
    ];
  }

  // Check if we should render anything
  if (!templatesData || templatesData.length === 0) {
    // console.log("No template data available, not rendering");
    return null;
  }

  if (!drawAllImages) {
    // console.log("drawAllImages is false, not rendering");
    return null;
  }

  // Use React Portal to ensure the elements are actually in the DOM
  return portalContainer.current
    ? ReactDOM.createPortal(
        <div ref={containerRef}>
          {templatesToRender.map((templateData, index) => (
            <div
              key={index}
              className="draw-template-container"
              style={{
                ...templateData.template.outerContainer,
                width: templateData.template.outerContainer.width,
                borderWidth: 0,
                padding: 0,
                margin: 0,
                position: "relative", // Ensure position is set
                display: "block", // Ensure display is set
              }}
            >
              <Rnd
                style={{ ...templateData.template.beforeImage.containerStyle }}
                size={{
                  width: templateData.template.beforeImage.size.width,
                  height: templateData.template.beforeImage.size.height,
                }}
                position={{
                  x: templateData.template.beforeImage.position.x,
                  y: templateData.template.beforeImage.position.y,
                }}
                enableResizing={false}
                disableDragging={true}
              >
                <img
                  alt=""
                  src={templateData.beforeCroppedImage}
                  style={{ ...templateData.template.beforeImage.imageStyle }}
                  onError={(e) => {
                    console.error("Before image failed to load:", e.target);
                    reportRenderError(
                      "⚠️ A before image failed to load in export. Please try again.",
                    );
                  }}
                />
              </Rnd>
              <Rnd
                style={{ ...templateData.template.afterImage.containerStyle }}
                size={{
                  width: templateData.template.afterImage.size.width,
                  height: templateData.template.afterImage.size.height,
                }}
                position={{
                  x: templateData.template.afterImage.position.x,
                  y: templateData.template.afterImage.position.y,
                }}
                enableResizing={false}
                disableDragging={true}
              >
                <img
                  alt=""
                  src={templateData.afterCroppedImage}
                  style={{ ...templateData.template.afterImage.imageStyle }}
                  onError={(e) => {
                    console.error("After image failed to load:", e.target);
                    reportRenderError(
                      "⚠️ An after image failed to load in export. Please try again.",
                    );
                  }}
                />
              </Rnd>
              {templateData.template.divs?.map((element, idx) => (
                <div key={idx} style={{ ...element }} />
              ))}
              {templateData.template.images?.map((additionalImage, idx) => {
                const resolvedOverlay = resolveOverlayForExport(
                  additionalImage,
                  assetUrlMap,
                );
                if (!resolvedOverlay) {
                  logExportDebug("overlay_skipped_placeholder_or_empty", {
                    overlayKind: additionalImage.kind || "standard",
                    overlayAssetId: additionalImage.assetId ?? null,
                    src: summarizeUrlForLogs(additionalImage.src),
                  });
                  return null;
                }

                const isRemoteOverlay = isRemoteHttpUrl(
                  resolvedOverlay.sourceUrl,
                );
                const resolvedSrc = isRemoteOverlay
                  ? (externalImageUrlMap[resolvedOverlay.sourceUrl] ??
                    TRANSPARENT_PIXEL_DATA_URL)
                  : resolvedOverlay.sourceUrl;

                const overlayLogKey = `${resolvedOverlay.sourceUrl}=>${resolvedSrc}`;
                if (!loggedResolvedOverlaySrcRef.current.has(overlayLogKey)) {
                  loggedResolvedOverlaySrcRef.current.add(overlayLogKey);
                  logExportDebug("overlay_resolved_src", {
                    overlayKind: resolvedOverlay.overlayKind,
                    overlayAssetId: resolvedOverlay.assetId,
                    sourceType: resolvedOverlay.sourceType,
                    src: summarizeUrlForLogs(resolvedOverlay.originalSrc),
                    resolvedSrc: summarizeUrlForLogs(resolvedOverlay.sourceUrl),
                    renderSrcType: resolvedSrc.startsWith("blob:")
                      ? "blob"
                      : resolvedSrc.startsWith("data:")
                        ? "data"
                        : "other",
                  });
                }

                return (
                  <Rnd
                    key={idx}
                    bounds="parent"
                    minHeight="5%"
                    minWidth="5%"
                    style={{ ...additionalImage.containerStyle }}
                    size={{
                      width: additionalImage.size.width,
                      height: additionalImage.size.height,
                    }}
                    position={{
                      x: additionalImage.position.x,
                      y: additionalImage.position.y,
                    }}
                    enableResizing={false}
                    disableDragging={true}
                  >
                    <img
                      src={resolvedSrc}
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        const errorSrc =
                          img.currentSrc || img.src || resolvedSrc;
                        if (!loggedOverlayErrorSrcRef.current.has(errorSrc)) {
                          loggedOverlayErrorSrcRef.current.add(errorSrc);
                          logExportDebug("overlay_image_error", {
                            overlayKind: resolvedOverlay.overlayKind,
                            overlayAssetId: resolvedOverlay.assetId,
                            sourceType: resolvedOverlay.sourceType,
                            src: summarizeUrlForLogs(
                              resolvedOverlay.originalSrc,
                            ),
                            resolvedSrc: summarizeUrlForLogs(
                              resolvedOverlay.sourceUrl,
                            ),
                            failedSrc: summarizeUrlForLogs(errorSrc),
                            renderSrcType: resolvedSrc.startsWith("blob:")
                              ? "blob"
                              : resolvedSrc.startsWith("data:")
                                ? "data"
                                : "other",
                          });
                        }
                        if (!hasReportedRenderErrorRef.current) {
                          console.error("Image loading error details:", {
                            src:
                              img.src?.substring(0, 100) +
                              (img.src?.length > 100 ? "..." : ""),
                            naturalWidth: img.naturalWidth,
                            naturalHeight: img.naturalHeight,
                            complete: img.complete,
                            currentSrc: img.currentSrc?.substring(0, 100),
                            time: new Date().toISOString(),
                          });
                        }
                        // Non-fatal: overlays can be missing when third-party/CDN
                        // assets do not send CORS headers. Keep export running.
                        console.warn(
                          "⚠️ Some stickers/assets failed to load in export. Saved image may miss overlays.",
                        );

                        // Provide a fallback for failed images. this is a 1x1 pixel transparent image.
                        img.src = TRANSPARENT_PIXEL_DATA_URL;
                      }}
                      data-overlay-kind={additionalImage.kind || "standard"}
                      data-overlay-censorship-side={
                        additionalImage.censorshipSide
                      }
                      data-overlay-region-key={additionalImage.regionKey}
                      style={{ ...additionalImage.imageStyle }}
                      alt=""
                    />
                  </Rnd>
                );
              })}
              {templateData.template.texts?.map((text, idx) => (
                <Rnd
                  key={idx}
                  enableResizing={false}
                  disableDragging={true}
                  style={{ ...text.containerStyle }}
                  position={{
                    x: text.position.x,
                    y: text.position.y,
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      ...text.textStyle,
                    }}
                  >
                    {text.content}
                  </p>
                </Rnd>
              ))}
            </div>
          ))}
        </div>,
        portalContainer.current,
      )
    : null;
};

export default DrawAllImages;
